// import { Filters } from "./Filters";
import { SearchResponse } from "@algolia/client-search";
import algoliasearch from "algoliasearch/lite";
import cx from "classnames";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { useDebounce } from "../hooks/useDebounce";
import {
  AlgoliaFacetsType,
  AlgoliaFacetType,
  AlgoliaHitType,
  FinderFilterType,
  FinderResultCardType,
  FinderRouterQueryType,
  PartnerSimpleType
} from "../types";
import styles from "./Finder.module.css";
import { Footer } from "./Footer";
import { Header } from "./Header";
import { Query } from "./Query";
import { ResultCard } from "./ResultCard";
const Filters = dynamic(() => import("./Filters").then((m) => m.Filters), {
  ssr: false,
});

const searchClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_APP_ID || "",
  process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY || ""
);
const index = searchClient.initIndex(
  process.env.NEXT_PUBLIC_ALGOLIA_INDEX || "Maak je Stap development"
);

type Props = {
  partners: PartnerSimpleType[];
  initialFilters: FinderFilterType[];
  pageSlug?: string;
};

export const FinderComponent = ({
  partners,
  initialFilters,
  pageSlug,
}: Props) => {
  const router = useRouter();

  // TODO: this creates a warning that serverside query isn't the same because I'm not using the router (is undefined on load)
  // TODO: probably should pass initialProps from page to avoid this
  const [query, setQuery] = useState<string>(getURLParam("q") || "");
  const [filters, setFilters] = useState<FinderFilterType[]>(
    getFiltersWithState(initialFilters)
  );

  const debouncedRouterQuery = useDebounce(router.query, 250);
  const [hits, setHits] = useState<FinderResultCardType[]>([]);
  const [state, setState] = useState<"loading" | "idle">("loading");

  const [currentPage, setCurrentPage] = useState<number>(0);
  const [numResultsPerPage, setNumResultsPerPage] = useState<number>(10);
  const [totalResults, setTotalResults] = useState<number>(0);
  const [totalVisibleResults, setTotalVisibleResults] = useState<number>(0);

  const update = React.useCallback((routerQuery: FinderRouterQueryType) => {
    if (routerQuery?.q?.trim() === "") delete routerQuery.q;
    if (pageSlug) routerQuery.slug = pageSlug;
    router.push(
      {
        query: routerQuery,
      },
      undefined,
      { shallow: true }
    );
  }, []);

  const onQueryChange = React.useCallback(
    (newQuery: string) => {
      const routerQuery: FinderRouterQueryType = {
        q: newQuery,
        ...serializeFilters(filters),
      };
      update(routerQuery);
    },
    [filters, update]
  );

  const onFiltersChange = React.useCallback(
    (newFilters: FinderFilterType[]) => {
      const routerQuery: FinderRouterQueryType = {
        q: query,
        ...serializeFilters(newFilters),
      };
      update(routerQuery);
    },
    [query, update]
  );

  const onPaginationChange = React.useCallback(() => {
    const routerQuery: FinderRouterQueryType = {
      q: query,
      page: `${currentPage + 1}`,
      ...serializeFilters(filters),
    };
    update(routerQuery);
  }, [query, currentPage, filters, update]);

  useEffect(() => {
    if (!router) return;
    setQuery((router.query.q as string) || "");
    setFilters(getFiltersWithState(initialFilters));
    setCurrentPage(parseInt(router?.query?.page as string) || 0);
  }, [router.query, initialFilters]);

  useEffect(() => {
    const facetFilters = filtersToFacets(filters);

    setState("loading");

    index
      .search(query, {
        facetFilters,
        page: currentPage,
      })
      .then((result: SearchResponse) => {
        // TODO: check for race condition on facetFilters as well
        if (result.query !== query) return;

        const tmpHits: AlgoliaHitType[] = result.hits as AlgoliaHitType[];

        setNumResultsPerPage(result.hitsPerPage);
        setTotalResults(result.nbHits);

        if (!currentPage) {
          setHits(tmpHits.map((item) => hitToResultCard(item, partners)));
        } else {
          setHits((hits) => [
            ...hits,
            ...tmpHits.map((item) => hitToResultCard(item, partners)),
          ]);
        }

        setState("idle");
      });
  }, [debouncedRouterQuery]);

  useEffect(() => {
    setTotalVisibleResults(
      Math.min(hits.length, numResultsPerPage * (currentPage + 1))
    );
  }, [currentPage, hits]);

  const onClearFilter = React.useCallback(
    ({ key, value }: { key: string; value: string }) => {
      const newFilters = JSON.parse(JSON.stringify(filters)).map(
        (filter: FinderFilterType) => {
          if (filter.key === key) {
            filter.options = filter.options.map((option) => ({
              ...option,
              checked: value === option.value ? false : Boolean(option.checked),
            }));
          }
          return filter;
        }
      );
      onFiltersChange(newFilters);
    },
    [filters, onFiltersChange]
  );

  const onClearAllFilters = React.useCallback(() => {
    const routerQuery: FinderRouterQueryType = {
      q: query,
    };
    update(routerQuery);
  }, [query]);

  return (
    <div className={styles.container}>
      <div className={styles.query}>
        <Query
          onChange={onQueryChange}
          value={query}
          placeholder="Cursussen, trainingen en opleidingen"
        />
      </div>
      <div className={styles.filters}>
        {filters && <Filters filters={filters} onChange={onFiltersChange} />}
      </div>
      <div className={styles.header}>
        <Header
          totalResults={totalResults}
          loading={state === "loading"}
          filters={filters}
          onClearFilter={onClearFilter}
          onClearAllFilters={onClearAllFilters}
        />
      </div>
      <div
        className={cx(styles.results, {
          [styles.loading]: state === "loading",
        })}
      >
        {hits.map((hit) => (
          <ResultCard key={hit._id} {...hit} />
        ))}
      </div>
      <div className={styles.footer}>
        <Footer
          totalResults={totalResults}
          totalVisibleResults={totalVisibleResults}
          onLoadMoreClick={onPaginationChange}
          loading={state === "loading"}
        />
      </div>
    </div>
  );
};

export const Finder = React.memo(FinderComponent);

/**
 * Get specific parameter from url, e.g `?page=1`
 */

function getURLParam(key: string) {
  const params =
    typeof window !== "undefined"
      ? new URL(location.href).searchParams
      : { get: () => "" };

  return params.get(key);
}

/**
 * Get all filters with checked options based on url
 */

function getFiltersWithState(filters: FinderFilterType[]) {
  if (typeof window === "undefined") return [];
  const newFilters = JSON.parse(JSON.stringify(filters)).map(
    (filter: FinderFilterType) => {
      const urlFilter = getURLParam(filter.key);
      if (urlFilter) {
        const urlFilterArr = urlFilter.split(",");
        filter.amountActive = urlFilterArr.length;
        filter.options = filter.options.map((option) => ({
          ...option,
          checked: urlFilterArr.indexOf(option.value) > -1,
        }));
      }
      return filter;
    }
  );
  return newFilters;
}

/**
 * Create url query object from filters
 */

function serializeFilters(filters: FinderFilterType[]): {} {
  const params = filters.reduce((prev, curr) => {
    const values = curr.options
      .filter(({ checked }) => checked)
      .map(({ value }) => value)
      .join(",");

    if (!values.length) return prev;

    return {
      ...prev,
      [curr.key]: values,
    };
  }, {});

  return params;
}

/**
 * Create result card from algolia search result object
 */

function hitToResultCard(
  hit: AlgoliaHitType,
  partners: PartnerSimpleType[]
): FinderResultCardType {
  const formatter = new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  });

  const partner = partners.filter(({ slug }) => slug === hit.opleider)[0];
  const obj: FinderResultCardType = {
    _id: hit.objectID + hit.naam,
    title: hit.naam,
    subtitle: partner?.name || "",
    logo: partner?.avatar || "",
    link: hit.url || "",
    properties: [
      {
        label: "Vakgebied",
        value: [
          hit.vakgebied1,
          hit.vakgebied2,
          hit.vakgebied3,
          hit.vakgebied4,
          hit.vakgebied5,
        ]
          .filter((v) => Boolean(v) && !v.includes("Kies"))
          .join(", "),
      },
      { label: "Studieduur", value: hit.duur },
      { label: "Type opleiding", value: hit.type },
      { label: "Locatie", value: hit.locatie },
      {
        label: "Kosten",
        value: hit.kosten ? formatter.format(+hit.kosten) : null,
      },
      { label: "Afrondingsbewijs", value: hit.afrondingsbewijs },
      { label: "Niveau", value: hit.niveau },
      {
        label: "Studiebelasting",
        value: hit.studiebelasting ? `${hit.studiebelasting} uur` : null,
      },
    ].filter(({ value }) => Boolean(value)),
    body: hit.beschrijving,
  };

  return obj;
}

/**
 * Create algolia facet from filters
 */

function filtersToFacets(filters: FinderFilterType[]): AlgoliaFacetsType {
  const facets: AlgoliaFacetsType = filters
    .reduce((prev: AlgoliaFacetsType, curr: FinderFilterType) => {
      const newFilters = curr.options
        .filter(({ checked }) => checked)
        .map(({ value }) => `${curr.key}:${value}`);

      return [...prev, newFilters];
    }, [])
    .filter((filter: AlgoliaFacetType) => Boolean(filter.length))

    // for 'vakgebied' we need to send extra parameters
    // to match `vakgebied1, vakgebied2, vakgebied3, vakgebied4, vakgebied5`
    // due to excel limitations on import
    .map((filter: AlgoliaFacetType) => {
      if (filter[0].indexOf("vakgebied") === 0) {
        return filter.reduce((prev: string[], curr: string) => {
          const additionalFacets = [1, 2, 3, 4, 5].map((i) =>
            curr.replace("vakgebied", `vakgebied${i}`)
          );
          return [...prev, ...additionalFacets];
        }, []);
      }

      return filter;
    });
  return facets;
}
